import React, { useContext, useReducer, createContext, ReactElement } from 'react'
import { ApolloProvider } from 'react-apollo-hooks'

import initialState from './initial'
import reducer from './reducers'
import client from './apollo'
import { ITour } from './queries';
import { IAppStoreAction } from './actions';
import { IToursFilter, IToursCategoriesValue } from './types';

export type Search = {
	query: string | null
}

export type User = {
	id: string
	god: boolean
}

export interface IAppState {
	// user: User | null
	// search: Search
	tour: ITour | null
	filter: IToursFilter
	categories: IToursCategoriesValue
}

const AppContext: React.Context<[IAppState, React.Dispatch<IAppStoreAction>]> = createContext<
	any
>(null)

export const AppProvider: React.FunctionComponent<{}> = ({
	children,
}) => (
	<ApolloProvider client={client}>
		<AppContext.Provider value={useReducer(reducer, initialState)}>
			{children}
		</AppContext.Provider>
	</ApolloProvider>
)

interface IStoreValueGenericProps {
    keys: string[]
    context: React.Context<[any, React.Dispatch<any>]>
}

export const StoreValueGeneric: React.FC<IStoreValueGenericProps> = ({ children, keys = [], context }) => {
    let [state, dispatch] = useContext(context);
    const result2: { [key:string]: any } = {}
    keys.forEach(key => {
        // Look for sub properties
        const keyChain = key.split(".")
        if (keyChain.length > 0) {
            const val = keyChain.reduce((a: any,c: string) => a[c], state)
            const last = keyChain[keyChain.length -1] // ts is too tedious if pop()
            result2[last] = val
        } else {
            result2[key] = state[key]
        }
    })
    if (keys.indexOf('dispatch') !== -1) result2['dispatch'] = dispatch
    
    let elements = React.Children.toArray(children)
    if (elements.length === 1) {
        return React.cloneElement(elements[0] as ReactElement, result2);
    } else if (elements.length > 0) {
        for (let i in elements) {
            elements[i] = React.cloneElement(elements[i] as ReactElement, result2)
        }
    }
    return <>{elements}</>
}

export interface IStoreValueProps {
    keys: string[]
}

// Using the hoc avoids some TS issue
// If the wrapped component has props, in jsx it will ask for those props, and either we make them optional which is what we want, either we make a hoc
export const withStoreValueFactory = (context: React.Context<[any, React.Dispatch<any>]>) => <SP extends object, XP extends object = {}>(WrappedComponent: React.ComponentType<XP & SP>, keys: string[]) => {
    return (props: XP) => {
		let [state, dispatch] = useContext(context);
		const result2: { [key:string]: any } = {}
		keys.forEach(key => {
			// Look for sub properties
			const keyChain = key.split(".")
			if (keyChain.length > 0) {
				const val = keyChain.reduce((a: any,c: string) => a[c], state)
				const last = keyChain[keyChain.length -1] // ts is too tedious if pop()
				result2[last] = val
			} else {
				result2[key] = state[key]
			}
		})
		if (keys.indexOf('dispatch') !== -1) result2['dispatch'] = dispatch

		return <WrappedComponent {...(props as XP)} {...(result2 as SP)}/>
	}
}

export const withAppStoreValue = withStoreValueFactory(AppContext)